/**
 * expenseListModel — reine Logik der Ausgaben-Liste: Fälligkeit, Filter,
 * Sortierung, CSV-Export (Semikolon, deutsche Dezimalzahlen, UTF-8 mit BOM für Excel).
 */
import { EXPENSE_STATUS } from './expenseStatus';
import { ZAHLUNGSARTEN } from '@/lib/expenseBooking';

const DAY = 24 * 60 * 60 * 1000;
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const de = (n) => round2(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });
const deDate = (iso) => (iso ? iso.slice(0, 10).split('-').reverse().join('.') : '');

export const EMPTY_FILTER = { suche: '', von: '', bis: '', kategorie: '', dokument: '', minNetto: '' };
const OPEN_VIEWS = ['offen', 'faellig', 'teilbezahlt'];

export const nettoOf = (row) => round2((row.positionen || []).reduce((s, p) => s + (Number(p.netto) || 0), 0));
export const steuerOf = (row) => round2((row.positionen || []).reduce((s, p) => s + (Number(p.steuer) || 0), 0));
export const offenOf = (row) => (OPEN_VIEWS.includes(row.view) ? Number(row.openItem?.open_amount ?? row.brutto) || 0 : 0);

/** Fälligkeit wie sevdesk: "Heute", "Morgen", "In 7 Tagen", "Seit 3 Tagen"; bezahlt → Datum durchgestrichen */
export function dueLabel(row, today) {
  if (row.view === 'bezahlt') {
    const paid = row.openItem?.last_payment_date || row.bezahlt_am || row.beleg_datum;
    return { text: deDate(paid) || '—', tone: 'paid' };
  }
  const due = row.openItem?.due_date || row.faellig_am;
  if (!OPEN_VIEWS.includes(row.view) || !due) return { text: '—', tone: 'neutral' };
  const days = Math.round((Date.parse(`${due}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / DAY);
  if (days === 0) return { text: 'Heute', tone: 'warn' };
  if (days === 1) return { text: 'Morgen', tone: 'neutral' };
  if (days > 1) return { text: `In ${days} Tagen`, tone: 'neutral' };
  return { text: `Seit ${-days} Tag${days === -1 ? '' : 'en'}`, tone: 'error' };
}

/** Suchtext einer Ausgabe: Lieferant, Nummern, Notiz, Positionen, Belegzeilen, Kategorien, Beträge */
function haystack(row, catName) {
  const amounts = [row.brutto, nettoOf(row), ...(row.positionen || []).flatMap((p) => [p.netto, p.steuer])]
    .filter((n) => Number(n)).flatMap((n) => [de(n), round2(n).toFixed(2)]);
  const cats = [row.category_id, ...(row.positionen || []).map((p) => p.category_id)]
    .map((id) => catName?.get(Number(id))).filter(Boolean);
  return [
    row.lieferant_name, row.beleg_nr, row.expense_no, row.beschreibung,
    ...(row.positionen || []).map((p) => p.beschreibung),
    ...(row.belegzeilen || []).map((z) => z.text),
    ...cats, ...amounts,
  ].filter(Boolean).join(' \n ').toLowerCase();
}

/** Stichwortsuche: alle Wörter müssen vorkommen (UND) */
export function matchesSearch(row, term, catName) {
  const words = String(term || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const text = haystack(row, catName);
  return words.every((w) => text.includes(w));
}

export function filterExpenses(rows, tab, f, catName = null) {
  const min = Number(String(f.minNetto || '').replace(',', '.'));
  return rows.filter((r) => (tab === 'alle' || r.view === tab)
    && matchesSearch(r, f.suche, catName)
    && (!f.von || (r.beleg_datum && r.beleg_datum >= f.von))
    && (!f.bis || (r.beleg_datum && r.beleg_datum <= f.bis))
    && (!f.kategorie || String(r.category_id) === String(f.kategorie)
      || (r.positionen || []).some((p) => String(p.category_id) === String(f.kategorie)))
    && (!f.dokument || (f.dokument === 'mit' ? !!r.file_path : !r.file_path))
    && (!f.minNetto || !Number.isFinite(min) || nettoOf(r) >= min));
}

const SORT_VALUE = {
  created_at: (r) => r.created_at || '',
  beleg_datum: (r) => r.beleg_datum || '',
  netto: (r) => nettoOf(r),
};

export function sortExpenses(rows, { key, dir }) {
  const get = SORT_VALUE[key] || SORT_VALUE.created_at;
  const sign = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = get(a);
    const vb = get(b);
    if (va === vb) return 0;
    return va > vb ? sign : -sign;
  });
}

const csvField = (v) => {
  const s = String(v ?? '');
  return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function toCsv(rows, categoriesById) {
  const head = ['Status', 'Belegnummer', 'Ausgabe-Nr.', 'Lieferant', 'Belegdatum', 'Netto', 'Steuer', 'Brutto', 'Offen', 'Kategorie', 'Konto', 'Zahlungsart'];
  const lines = rows.map((r) => {
    const cat = categoriesById.get(Number(r.category_id));
    return [
      EXPENSE_STATUS[r.view]?.label || r.view, r.beleg_nr, r.expense_no, r.lieferant_name, deDate(r.beleg_datum),
      de(nettoOf(r)), de(steuerOf(r)), de(r.brutto), de(offenOf(r)), cat?.name, cat?.account_no,
      r.zahlungsart && r.status === 'gebucht' ? ZAHLUNGSARTEN[r.zahlungsart]?.label : '',
    ].map(csvField).join(';');
  });
  return `\uFEFF${[head.join(';'), ...lines].join('\n')}\n`;
}
